/**
 * Pestaña Resumen: el `MatchReport` del API tal cual lo publica el contrato
 * (fundamentos por equipo y por jugador, sideout/break y puntos por razón) más
 * la ATRIBUCIÓN DE PUNTOS, que es el desglose que hace que la columna cierre
 * con el marcador.
 *
 * Los dos bloques de puntos de esta pantalla NO son el mismo número y por eso
 * están separados y rotulados:
 *
 *  - **Puntos por razón** — lo que DECLARÓ el estadístico al teclear el punto
 *    (`point.reason`). Sirve para leer la intención del tecleo.
 *  - **Atribución de puntos** — lo que se DERIVA del log (último toque del
 *    equipo que ganó el rally). Es el único que tiene autor, y el que suma
 *    exactamente el marcador: `total = jugadores + sin asignar + errores del
 *    rival`.
 *
 * Cuando los dos discrepan manda el toque; el detalle está en la cabecera de
 * `packages/stats/src/match-report.ts`.
 */
import type { MatchReport, PlayerReport, SkillLine, TeamReport } from '@scout/core';
import { Card } from '../../components/States';
import { PlayersTable, TeamSkillsTable } from '../../components/SkillTable';
import { useT } from '../../i18n/use-t';
import { effPct, pct, playerLabel, reasonLabel } from '../../lib/format';
import {
  pointsAddUp,
  readPlayerPoints,
  readTeamPoints,
  type PlayerPoints,
  type TeamPoints,
} from '../../lib/projections';

function PointsByReason({ points }: { points: Record<string, number> }): JSX.Element {
  const t = useT();
  const entries = Object.entries(points).filter(([, value]) => value > 0);
  if (entries.length === 0) return <p className="empty">{t('summary.noPoints')}</p>;
  const total = entries.reduce((sum, [, value]) => sum + value, 0);
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('summary.pointBy')}</th>
            <th className="num">{t('summary.count')}</th>
            <th className="num">{t('summary.percent')}</th>
          </tr>
        </thead>
        <tbody>
          {entries
            .sort((a, b) => b[1] - a[1])
            .map(([reason, value]) => (
              <tr key={reason}>
                <td>{reasonLabel(reason, t)}</td>
                <td className="num">{value}</td>
                <td className="num">{pct(value / total)}</td>
              </tr>
            ))}
          <tr className="total-row">
            <td>{t('common.total')}</td>
            <td className="num">{total}</td>
            <td className="num">100%</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Jugador con puntos propios, ya leído del reporte. */
interface ScorerRow extends PlayerPoints {
  number: number;
  name?: string;
}

function scorersOf(players: readonly PlayerReport[]): ScorerRow[] {
  const out: ScorerRow[] = [];
  for (const player of players) {
    const points = readPlayerPoints(player);
    if (!points || points.total === 0) continue;
    out.push(
      player.name === undefined
        ? { ...points, number: player.number }
        : { ...points, number: player.number, name: player.name },
    );
  }
  // Ranking de anotadores: el DT lee de arriba para abajo.
  return out.sort((a, b) => b.total - a.total || a.number - b.number);
}

/**
 * La cuenta que cierra con el marcador. La fila de total no es decorativa: es
 * la que se suma en voz alta delante del cliente, así que se muestra siempre y
 * se avisa —en vez de esconderlo— si por algún motivo no cerrara.
 */
function PointsAttribution({
  points,
  players,
}: {
  points: TeamPoints;
  players: readonly PlayerReport[];
}): JSX.Element {
  const t = useT();
  const scorers = scorersOf(players);
  const rows: Array<{ key: string; label: string; value: number; hint: string }> = [
    {
      key: 'byPlayer',
      label: t('summary.pointsByPlayer'),
      value: points.byPlayer,
      hint: t('summary.pointsByPlayerHint'),
    },
    {
      key: 'unassigned',
      label: t('summary.pointsUnassigned'),
      value: points.unassigned,
      hint: t('summary.pointsUnassignedHint'),
    },
    {
      key: 'opponentErrors',
      label: t('summary.pointsOpponentErrors'),
      value: points.opponentErrors,
      hint: t('summary.pointsOpponentErrorsHint'),
    },
  ];

  return (
    <>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('summary.pointsOrigin')}</th>
              <th className="num">{t('summary.count')}</th>
              <th>{t('summary.pointsMeans')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td className="num">{row.value}</td>
                <td className="hint">{row.hint}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td>{t('summary.pointsScoreboard')}</td>
              <td className="num">{points.total}</td>
              <td className="hint">{t('summary.pointsScoreboardHint')}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {pointsAddUp(points) ? (
        <p className="hint">
          {t('summary.pointsIdentity', {
            total: points.total,
            byPlayer: points.byPlayer,
            unassigned: points.unassigned,
            opponentErrors: points.opponentErrors,
          })}
        </p>
      ) : (
        <p className="hint neg">{t('summary.pointsMismatch')}</p>
      )}

      <h3 style={{ marginTop: 16 }}>{t('summary.pointsByPlayerTitle')}</h3>
      {scorers.length === 0 ? (
        <p className="empty">{t('summary.noScorers')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('summary.player')}</th>
                <th className="num" title={t('summary.aceTitle')}>
                  {t('summary.ace')}
                </th>
                <th className="num" title={t('summary.killTitle')}>
                  {t('summary.kill')}
                </th>
                <th className="num" title={t('summary.blockTitle')}>
                  {t('summary.block')}
                </th>
                <th className="num">{t('common.total')}</th>
              </tr>
            </thead>
            <tbody>
              {scorers.map((row) => (
                <tr key={row.number}>
                  <td>{playerLabel(row.number, row.name)}</td>
                  <td className="num">{row.serve}</td>
                  <td className="num">{row.attack}</td>
                  <td className="num">{row.block}</td>
                  <td className="num">{row.total}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>{t('common.total')}</td>
                <td className="num">{points.byOwnSkill.serve}</td>
                <td className="num">{points.byOwnSkill.attack}</td>
                <td className="num">{points.byOwnSkill.block}</td>
                <td className="num">{points.byPlayer + points.unassigned}</td>
              </tr>
            </tbody>
          </table>
          {/* El total por fundamento incluye la acción propia SIN camiseta, así
              que puede superar la suma de la columna de arriba: la diferencia
              es exactamente `sin asignar`. */}
          {points.unassigned > 0 ? (
            <p className="hint">
              {t('summary.pointsUnassignedNote', { count: points.unassigned })}
            </p>
          ) : null}
        </div>
      )}
    </>
  );
}

/** Recepción positiva: (# + +) / total, la lectura de "Rec +" de toda planilla. */
function positive(line: SkillLine): number {
  if (line.total === 0) return 0;
  return ((line.byEvaluation['#'] ?? 0) + (line.byEvaluation['+'] ?? 0)) / line.total;
}

function TeamPanel({ team, side }: { team: TeamReport; side: 'home' | 'away' }): JSX.Element {
  const t = useT();
  const points = readTeamPoints(team);
  const attack = team.skills['attack'];
  const reception = team.skills['reception'];
  return (
    <Card title={<span className={`side-name ${side} team-title`}>{team.name}</span>}>
      {/* Tarjetas de lo que se mira primero. El rótulo va ANTES que el valor en
          el DOM (y la hoja los invierte): así se lee "Sideout 55%" también
          sin estilos, con un lector de pantalla o al copiar. */}
      <div className="kpis">
        {points ? (
          <div className={`kpi ${side}`} title={t('summary.pointsChipTitle')}>
            <span className="kpi-label">
              {t('matchViews.kpiPoints', { byPlayer: points.byPlayer })}
            </span>{' '}
            <span className="kpi-value">{points.total}</span>
          </div>
        ) : null}
        <div className="kpi" title={t('summary.sideoutTitle')}>
          <span className="kpi-label">{t('matchViews.kpiSideout')}</span>{' '}
          <span className="kpi-value">{pct(team.sideoutPct)}</span>
        </div>
        <div className="kpi" title={t('summary.breakTitle')}>
          <span className="kpi-label">{t('matchViews.kpiBreak')}</span>{' '}
          <span className="kpi-value">{pct(team.breakPct)}</span>
        </div>
        {attack !== undefined && attack.total > 0 ? (
          <div className="kpi">
            <span className="kpi-label">{t('matchViews.kpiAttack')}</span>{' '}
            <span className="kpi-value">{effPct(attack.efficiency)}</span>
          </div>
        ) : null}
        {reception !== undefined && reception.total > 0 ? (
          <div className="kpi">
            <span className="kpi-label">{t('matchViews.kpiReception')}</span>{' '}
            <span className="kpi-value">{pct(positive(reception))}</span>
          </div>
        ) : null}
      </div>

      <h3>{t('summary.bySkill')}</h3>
      <TeamSkillsTable skills={team.skills} />

      <h3 style={{ marginTop: 16 }}>{t('summary.pointsAttribution')}</h3>
      {points ? (
        <PointsAttribution points={points} players={team.players} />
      ) : (
        // Un API anterior a esta ola no publica el desglose. Se dice, en vez de
        // dibujar una tabla en cero que parece un partido sin cargar.
        <p className="empty">{t('summary.noPointsBreakdown')}</p>
      )}

      <h3 style={{ marginTop: 16 }}>{t('summary.pointsByReason')}</h3>
      <PointsByReason points={team.pointsByReason} />
      <h3 style={{ marginTop: 16 }}>{t('summary.players')}</h3>
      <PlayersTable players={team.players} />
    </Card>
  );
}

export function SummaryTab({ report }: { report: MatchReport }): JSX.Element {
  return (
    <div>
      <TeamPanel team={report.home} side="home" />
      <TeamPanel team={report.away} side="away" />
    </div>
  );
}
